'use client';

import { FC, useState, useCallback } from 'react';
import { Dialog, DialogContent, Box, DialogActions, Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { ProjectGridCore, ProjectRowType } from '@/components/tables/ProjectGridCore';
import { ProjectSearchPanel } from '@/components/tables/search/ProjectSearchPanel';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import type { ProjectSearchCriteria } from '@/api/ApiClient';
import { useProjectFilterStore } from '@/stores/projectFilterStore';

interface ProjectSelectionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Title of the dialog */
  title?: string;
  /** Callback when a project is selected */
  onProjectSelect: (project: ProjectRowType) => void;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** Whether to allow multiple selection (default: false) */
  allowMultiple?: boolean;
  /** Callback when multiple projects are selected (only used if allowMultiple is true) */
  onProjectsSelect?: (projects: ProjectRowType[]) => void;
}

const OPTION_KEYS: OptionKey[] = [
  'synthesisTechniques',
  'materials',
  'staff',
  'projectType',
  'projectStatus',
  'researcherOptions',
];

type ProjectSelectionDialogBodyProps = {
  title: string;
  onProjectSelect: (project: ProjectRowType) => void;
  onClose: () => void;
  allowMultiple: boolean;
  onProjectsSelect?: (projects: ProjectRowType[]) => void;
};

const ProjectSelectionDialogBody: FC<ProjectSelectionDialogBodyProps> = ({
  title,
  onProjectSelect,
  onClose,
  allowMultiple,
  onProjectsSelect,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ ids: new Set(), type: 'include' });
  const [selectedProjects, setSelectedProjects] = useState<ProjectRowType[]>([]);
  const [localFilters, setLocalFilters] = useState<ProjectSearchCriteria>(() => ({
    ...useProjectFilterStore.getState().filters,
  }));
  const [localPaginationModel, setLocalPaginationModel] = useState(() => ({
    ...useProjectFilterStore.getState().paginationModel,
  }));

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    setSelectionModel(newSelectionModel);
  };

  const handleProjectSelect = (project: ProjectRowType) => {
    if (allowMultiple) {
      // Multiple mode: selection confirmed via "Use Selection"
    } else {
      onProjectSelect(project);
      handleClose();
    }
  };

  const handleProjectsSelect = (projects: ProjectRowType[]) => {
    setSelectedProjects(projects);
  };

  const handleUseSelection = () => {
    if (allowMultiple) {
      if (selectionModel.ids.size > 0 && selectedProjects.length > 0) {
        onProjectsSelect?.(selectedProjects);
        handleClose();
      }
    }
  };

  const canUseSelection = allowMultiple ? selectionModel.ids.size >= 1 : false;

  return (
    <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: '1px solid #e0e0e0', px: 2, pt: 1, pb: 0.5 }}>
        <ProjectSearchPanel
          title={title}
          isSelectionMode={true}
          filters={localFilters}
          onFiltersChange={setLocalFilters}
          paginationModel={localPaginationModel}
          onPaginationModelChange={setLocalPaginationModel}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <ProjectGridCore
          showLinks={false}
          enableSelection={true}
          selectionModel={selectionModel}
          onSelectionChange={handleSelectionChange}
          checkboxSelection={allowMultiple}
          disableColumnMenu={true}
          showToolbar={false}
          density="compact"
          onProjectSelect={handleProjectSelect}
          onProjectsSelect={handleProjectsSelect}
          optionsData={options}
          optionsLoading={optionsLoading}
          filters={localFilters}
          paginationModel={localPaginationModel}
          onPaginationModelChange={setLocalPaginationModel}
        />
      </Box>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleUseSelection} variant="contained" disabled={!canUseSelection}>
          Use Selection
        </Button>
      </DialogActions>
    </DialogContent>
  );
};

export const ProjectSelectionDialog: FC<ProjectSelectionDialogProps> = ({
  open,
  title = 'Select Project',
  onProjectSelect,
  onClose,
  allowMultiple = false,
  onProjectsSelect,
}) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      {open ? (
        <ProjectSelectionDialogBody
          key="project-selection"
          title={title}
          onProjectSelect={onProjectSelect}
          onClose={handleClose}
          allowMultiple={allowMultiple}
          onProjectsSelect={onProjectsSelect}
        />
      ) : null}
    </Dialog>
  );
};
